import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout, Card, CustomerInviteWidget, ProjectTimelineWidget, NotificationsWidget } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { MobileCreateProjectButton } from '../components/MobileCreateProjectButton';
import { useAuth } from '../context/AuthContext';
import { customerApi, projectApi, contractApi, systemApi, projectStatusApi, userPreferencesApi, milestoneApi, companyApi, companyDashboardApi, contractConfigApi, adyenOnboardingApi, messageThreadApi, type AdyenAccount, type CompanyDashboardFinance } from '../services/api';
import { countProjectsByStatus, loadGlobalTemplate, type StatusStep } from '../services/projectStatusUtils';
import CompanyKeyFiguresPanel from '../components/CompanyKeyFigures';
import { TILE_KPI_IDS } from '../utils/kpiTiles';
import { KPI_REGISTRY } from '../constants/kpiRegistry';
import SectionPanel from '../components/SectionPanel';
import CompanyTodoList, { type CompanyTodoRow } from '../components/CompanyTodoList';
import { useProjectCustomerTodos } from '../hooks/useProjectCustomerTodos';
import { getActiveCompanyTodos, companyObjectionStages, type CompanyTodoId } from '../utils/companyTodos';
import { pendingAttestationMilestones } from '../utils/pendingAttestation';
import KpiSidebar from '../components/KpiSidebar';
import { CompanyOnboardingGuide } from '../components/CompanyOnboardingGuide';
import { isPracticalInfoComplete } from '../utils/practicalInfo';
import { useAdyenOnboardingPrompt } from '../hooks/useAdyenOnboardingPrompt';
import type { Project, SystemInfo, Company, ProjectFundingStatus, MessageThread } from '../types';
import { useRefreshOnReturn } from '../hooks/useRefreshOnReturn';
import { useRefreshOnNotification } from '../hooks/useRefreshOnNotification';
import { useReportTodoCount } from '../navigation/todoCount';
import { TODO_ANCHOR } from '../navigation/appNavigation';

const CARD_REFRESH_INTERVAL_MS = 60_000;

// Etappio's suggestion when neither the user nor admin has chosen anything
// that still exists: the key figures from ClickUp 869f91d22 and the chart.
// Mina Projekt is always shown and is not a choice.
const DEFAULT_COMPANY_KPIS = [
  'completedVolume', 'totalPaid', 'outstandingBalance', 'totalContractValue', 'activeVsCompleted',
  'projectStatusChart'
];


const badgeToHex: Record<string, string> = {
  secondary: '#6c757d',
  info: '#17a2b8',
  primary: '#007bff',
  warning: '#ffc107',
  success: '#28a745',
  danger: '#dc3545'
};

const fallbackStatusColors: Record<string, string> = {
  not_started: '#6c757d',
  contract: '#17a2b8',
  payment: '#ffc107',
  in_progress: '#007bff',
  milestone: '#28a745',
  on_hold: '#fd7e14',
  completed: '#28a745',
  objection: '#e8890c',
  conflict: '#dc3545',
  dispute: '#b02a37'
};

/** Chart buckets: the template's statuses with Invändning just before Konflikt (ClickUp 869fh4948). */
const withObjectionKey = (statusKeys: string[]): string[] => {
  if (statusKeys.includes('objection')) return statusKeys;
  const at = statusKeys.indexOf('conflict');
  return at < 0 ? [...statusKeys, 'objection'] : [...statusKeys.slice(0, at), 'objection', ...statusKeys.slice(at)];
};

const shouldSyncSigning = (contract: any) =>
  !!contract.externalSigningProcessId &&
  ['sent', 'pending_signature'].includes(contract.signingStatus || '');

const syncSigningContracts = async (contracts: any[]) => {
  const syncedContracts = await Promise.all(
    contracts.map(async (contract) => {
      if (!shouldSyncSigning(contract)) return contract;
      try {
        const synced = await contractApi.syncSigning(contract.id);
        return synced.contract;
      } catch {
        return contract;
      }
    })
  );
  return syncedContracts;
};

const capabilityIsValid = (value: unknown): boolean => {
  if (!value || typeof value !== 'object') return false;
  const capability = value as { allowed?: unknown; verificationStatus?: unknown; status?: unknown };
  return capability.allowed === true || capability.verificationStatus === 'valid' || capability.status === 'valid';
};

const hasLocalMockAdyenId = (value: string | null | undefined, prefix: string): boolean =>
  value ? new RegExp(`^${prefix}-[0-9a-f-]{20,}$`, 'i').test(value) : false;

const hasLocalMockAdyenAccountIds = (account: AdyenAccount | null): boolean =>
  !!account &&
  (hasLocalMockAdyenId(account.adyenAccountHolderId, 'AH') ||
    hasLocalMockAdyenId(account.adyenLegalEntityId, 'LE') ||
    hasLocalMockAdyenId(account.adyenBalanceAccountId, 'BA') ||
    hasLocalMockAdyenId(account.transferInstrumentId, 'SE'));

const hasRequiredAdyenCapabilities = (account: AdyenAccount | null): boolean => {
  const required = account?.requiredCapabilities ?? [];
  return (
    !!account &&
    !hasLocalMockAdyenAccountIds(account) &&
    required.length > 0 &&
    required.every((capability) => capabilityIsValid(account.capabilities?.[capability]))
  );
};

const isMockAdyenOnboardingUrl = (url: string | null | undefined): boolean => {
  if (!url) return false;
  try {
    return new URL(url, window.location.origin).hostname === 'mock.adyen.local';
  } catch {
    return url.includes('mock.adyen.local');
  }
};

export function Home() {
  const { t, i18n } = useTranslation();
  const kpiLang = i18n.language.startsWith('en') ? 'en' : 'sv';
  const navigate = useNavigate();
  const { user } = useAuth();
  const showAdyenOnboardingPrompt = useAdyenOnboardingPrompt(user?.personaType === 'company' && !user?.isAdmin);
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  // "Lägg till beställare" and reminders (ClickUp 869fmxjgt).
  const customerTodoRows = useProjectCustomerTodos(allProjects, () => void loadStats());
  const [templateSteps, setTemplateSteps] = useState<StatusStep[]>([]);
  const [chartData, setChartData] = useState<Record<string, number>>({});
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [systemInfo, setSystemInfo] = useState<SystemInfo | null>(null);
  const [companyName, setCompanyName] = useState('');
  const [company, setCompany] = useState<Company | null>(null);
  const [inviteFocusToken, setInviteFocusToken] = useState(0);
  const inviteWidgetRef = useRef<HTMLDivElement>(null);
  // Synchronous first-paint hint (per browser) so a returning collapsed user
  // renders minimized immediately — no "open then slide closed" flash. The
  // per-user server preference (loaded below) is authoritative and reconciles
  // this. Open by default when there is no hint (first-time visible).
  const [welcomeCollapsed, setWelcomeCollapsed] = useState(
    () => localStorage.getItem('welcomeWidgetCollapsedHint') === 'true'
  );
  // The slide transition is only enabled for explicit user toggles — never
  // when applying the loaded preference.
  const [welcomeAnimate, setWelcomeAnimate] = useState(false);
  const [projectStatusMap, setProjectStatusMap] = useState<Record<string, any[]>>({});
  const [selectedKpis, setSelectedKpis] = useState<string[]>([]);
  const [showKpiSidebar, setShowKpiSidebar] = useState(false);
  const [allContracts, setAllContracts] = useState<any[]>([]);
  // The server's funding status per project – the same source as the project
  // view, so the cards offer attestation exactly when the project does.
  const [fundingByProject, setFundingByProject] = useState<Record<string, ProjectFundingStatus>>({});
  const [allMilestones, setAllMilestones] = useState<any[]>([]);
  // Stage threads, for whose turn it is in an objection (ClickUp 869fmv7b5).
  const [messageThreads, setMessageThreads] = useState<MessageThread[]>([]);
  const [allCustomers, setAllCustomers] = useState<any[]>([]);
  const [dashboardFinance, setDashboardFinance] = useState<CompanyDashboardFinance | null>(null);
  const [adyenAccount, setAdyenAccount] = useState<AdyenAccount | null>(null);
  // Value unused since the standalone KYC card was removed; the setter still
  // gates the Adyen fetch effect below.
  const [, setIsLoadingAdyenAccount] = useState(user?.personaType === 'company' && !user?.isAdmin);
  const [isStartingKundkannedom, setIsStartingKundkannedom] = useState(false);
  const [kundkannedomMessage, setKundkannedomMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [pendingContracts, setPendingContracts] = useState<Array<{
    projectId: string;
    projectNumber: number;
    contractId: string;
    contractNumber: number;
    contractType?: 'fixed' | 'variation';
    variationNumber?: number | null;
    parentContractNumber?: number | null;
    description: string;
    contractAmount: string;
    draftStatus: string;
    notices?: Array<{ id: string; type: 'comment' | 'rejection'; message: string; authorName: string | null; byRole: string | null; createdAt: string }>;
  }>>([]);

  useEffect(() => {
    loadStats();
    loadSystemInfo();
    companyApi.getCompany().then(c => { setCompanyName(c.companyName || ''); setCompany(c); }).catch(() => {});
    loadGlobalTemplate().then(steps => setTemplateSteps(steps)).catch(() => {});
    contractApi.getPendingReview().then(data => setPendingContracts(data)).catch(() => {});

    // Load KPI preferences. Use the user's own selection when they have
    // configured one; otherwise fall back to the latest admin-configured
    // defaults (read via the authenticated /system endpoint so company
    // users — not just admins — get the current configuration).
    userPreferencesApi.getPreferences().then(prefs => {
      // Saved choices of KPIs that no longer exist are ignored; with nothing
      // left, the admin defaults (or Etappio's suggestion) apply.
      const savedKpis = Array.isArray(prefs.selectedKpis)
        ? prefs.selectedKpis.filter((id: string) => KPI_REGISTRY.some(kpi => kpi.id === id))
        : [];
      if (savedKpis.length > 0) {
        setSelectedKpis(savedKpis);
      } else {
        systemApi.getKpiDefaults().then(defaults => {
          // Old defaults can hold only legacy ids with no tile or block; then
          // Etappio's suggestion applies.
          const known = (defaults.enabledKpis ?? []).filter(id => KPI_REGISTRY.some(kpi => kpi.id === id));
          setSelectedKpis(known.length > 0 ? known : DEFAULT_COMPANY_KPIS);
        }).catch(() => {
          setSelectedKpis(DEFAULT_COMPANY_KPIS);
        });
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.personaType !== 'company' || user?.isAdmin) {
      setIsLoadingAdyenAccount(false);
      return;
    }

    setIsLoadingAdyenAccount(true);
    adyenOnboardingApi
      .getStatus()
      .then(account => setAdyenAccount(account))
      .catch(() => {
        setAdyenAccount(null);
        setKundkannedomMessage({
          type: 'error',
          text: t('home.kycFetchError')
        });
      })
      .finally(() => setIsLoadingAdyenAccount(false));
  }, [t, user?.isAdmin, user?.personaType]);


  const loadSystemInfo = async () => {
    try {
      const info = await systemApi.getSystemInfo();
      setSystemInfo(info);
    } catch (error) {
      console.error('Failed to load system info:', error);
    }
  };

  useEffect(() => {
    updateChartData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allProjects, fromDate, toDate, templateSteps]);

  useEffect(() => {
    if (allContracts.length === 0) return;
    Promise.all(allContracts.map((c: any) =>
      milestoneApi.getMilestones(c.id).catch(() => [])
    )).then(results => setAllMilestones(results.flat()));
  }, [allContracts]);

  // Persist explicit user choices as a per-user setting on the server, so the
  // welcome panel follows the logged-in user and not just this browser.
  const setWelcomeCollapsedByUser = (collapsed: boolean) => {
    setWelcomeAnimate(true);
    setWelcomeCollapsed(collapsed);
    localStorage.setItem('welcomeWidgetCollapsedHint', String(collapsed));
    userPreferencesApi.savePreference('welcomeWidgetCollapsed', collapsed).catch(() => {});
  };

  // Guide "Bjud in kund" action: scroll to the invite widget and signal it to
  // expand + focus its name field (no page navigation).
  const handleGuideInviteCustomer = () => {
    inviteWidgetRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setInviteFocusToken((token) => token + 1);
  };

  // Load the logged-in user's saved preference. If they have one, apply it
  // WITHOUT animation so a collapsed widget stays minimized from the start.
  // If not (first time this user logs in), keep it visible for this session
  // but remember that the user has seen it. The welcome video sits inside this
  // panel, so automatically collapsing it immediately makes the video disappear
  // before the user has had a fair chance to watch it.
  useEffect(() => {
    userPreferencesApi.getPreferences().then(prefs => {
      if (typeof prefs.welcomeWidgetCollapsed === 'boolean') {
        setWelcomeCollapsed(prefs.welcomeWidgetCollapsed);
        localStorage.setItem('welcomeWidgetCollapsedHint', String(prefs.welcomeWidgetCollapsed));
      } else {
        setWelcomeCollapsed(false);
        localStorage.setItem('welcomeWidgetCollapsedHint', 'true');
        userPreferencesApi.savePreference('welcomeWidgetCollapsed', true).catch(() => {});
      }
    }).catch(() => {
      setWelcomeCollapsed(false);
    });
  }, []);

  const handleSaveKpis = async (kpis: string[]) => {
    setSelectedKpis(kpis);
    await userPreferencesApi.savePreference('selectedKpis', kpis);
  };

  const loadStats = async () => {
    try {
      const [customers, projects, finance] = await Promise.all([
        customerApi.getCustomers(),
        projectApi.getProjects(),
        companyDashboardApi.getFinance().catch(() => null)
      ]);

      setAllProjects(projects);
      setDashboardFinance(finance);
      messageThreadApi.list().then(setMessageThreads).catch(() => setMessageThreads([]));

      // Load all contracts across projects
      const contractPromises = projects.map((p: any) =>
        contractApi.getContracts(p.id).catch(() => [])
      );
      Promise.all(projects.map((p: any) =>
        contractConfigApi.getProjectFunding(p.id)
          .then(funding => [p.id, funding] as const)
          .catch(() => null)
      )).then(results => {
        setFundingByProject(Object.fromEntries(
          results.filter((entry): entry is readonly [string, ProjectFundingStatus] => entry !== null)
        ));
      });

      Promise.all(contractPromises).then(async results => {
        const syncedContracts = await syncSigningContracts(results.flat());
        setAllContracts(syncedContracts);
        contractApi.getPendingReview().then(data => setPendingContracts(data)).catch(() => {});
      });

      // Load project status instances
      const statusPromises = projects.map((p: any) =>
        projectStatusApi.getInstance(p.id)
          .then(instance => ({ projectId: p.id, steps: instance.steps || [] }))
          .catch(() => ({ projectId: p.id, steps: [] }))
      );
      Promise.all(statusPromises).then(results => {
        const map: Record<string, any[]> = {};
        results.forEach(r => { map[r.projectId] = r.steps; });
        setProjectStatusMap(map);
      });

      setAllCustomers(customers);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  // Keep the project cards current: a started or attested stage (by the
  // customer, elsewhere) shows up when the user returns to the tab, and while
  // the page stays open, without a manual refresh.
  useRefreshOnReturn(loadStats);
  // …and as soon as a new notification arrives (e.g. a contract sent back).
  useRefreshOnNotification(loadStats);
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') void loadStats();
    }, CARD_REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateChartData = () => {
    let filteredProjects = allProjects;

    if (fromDate) {
      filteredProjects = filteredProjects.filter(p => p.createdAt >= fromDate);
    }
    if (toDate) {
      filteredProjects = filteredProjects.filter(p => p.createdAt <= toDate + 'T23:59:59');
    }

    // Build chart data dynamically from template steps
    const keys = templateSteps.length > 0
      ? templateSteps.map(s => s.key)
      : ['not_started', 'contract', 'payment', 'in_progress', 'milestone', 'on_hold', 'completed', 'conflict', 'dispute'];

    setChartData(countProjectsByStatus(filteredProjects, withObjectionKey(keys)));
  };

  // Build statusLabels dynamically from template steps with i18n fallback
  const statusLabels: Record<string, string> = templateSteps.length > 0
    ? { ...Object.fromEntries(templateSteps.map(s => [s.key, s.label_sv])), objection: t('home.statusObjection') }
    : {
        not_started: t('home.statusNotStarted'),
        contract: t('home.statusContract'),
        payment: t('home.statusPayment'),
        in_progress: t('home.statusInProgress'),
        milestone: t('home.statusMilestone'),
        on_hold: t('home.statusOnHold'),
        completed: t('home.statusCompleted'),
        objection: t('home.statusObjection'),
        conflict: t('home.statusConflict'),
        dispute: t('home.statusDispute')
      };

  // Build statusColors dynamically from template steps badge colors
  const statusColors: Record<string, string> = templateSteps.length > 0
    ? {
        ...Object.fromEntries(
          templateSteps.map(s => [s.key, badgeToHex[s.badgeColor] || fallbackStatusColors[s.key] || '#6c757d'])
        ),
        objection: fallbackStatusColors.objection
      }
    : fallbackStatusColors;

  const getMaxCount = () => {
    const values = Object.values(chartData);
    return Math.max(...values, 1);
  };



  // Kom igång-guide: derive the four onboarding statuses from data already
  // loaded on this page. KYC counts as done when Adyen has approved it.
  const onboardingSteps = {
    customerInvited: allCustomers.length > 0,
    kycApproved:
      adyenAccount?.kycStatus === 'passed' ||
      hasRequiredAdyenCapabilities(adyenAccount),
    projectCreated: allProjects.length > 0,
    practicalInfoComplete: company ? isPracticalInfoComplete(company) : false
  };
  const showCompanyGuide = user?.personaType === 'company' && !user?.isAdmin;

  const handleStartKundkannedom = async () => {
    setIsStartingKundkannedom(true);
    setKundkannedomMessage(null);
    try {
      const account = await adyenOnboardingApi.start();
      setAdyenAccount(account);
      if (account.onboardingUrl && !isMockAdyenOnboardingUrl(account.onboardingUrl)) {
        window.open(account.onboardingUrl, '_blank', 'noopener,noreferrer');
      } else if (account.onboardingUrl) {
        setKundkannedomMessage({
          type: 'success',
          text: t('home.kycMockDone')
        });
      } else {
        setKundkannedomMessage({
          type: 'success',
          text: t('home.kycStarted')
        });
      }
    } catch {
      setKundkannedomMessage({
        type: 'error',
        text: t('home.kycStartError')
      });
    } finally {
      setIsStartingKundkannedom(false);
    }
  };

  // "Att göra nu" — active action items, sorted by the product-decided group
  // order. Conditions come from data already loaded on this page.
  const objectionStages = companyObjectionStages(allMilestones, messageThreads);
  const activeTodos = getActiveCompanyTodos({
    showAdyenOnboarding: showAdyenOnboardingPrompt,
    customersCount: allCustomers.length,
    projectsCount: allProjects.length,
    adyenAccount,
    kycApproved:
      adyenAccount?.kycStatus === 'passed' || hasRequiredAdyenCapabilities(adyenAccount),
    vatNumber: company?.vatNumber,
    companyLoaded: !!company,
    practicalInfoComplete: company ? isPracticalInfoComplete(company) : false,
    pendingAttestationsCount: pendingAttestationMilestones(allContracts, allMilestones).length,
    disputedMilestonesCount: objectionStages.length,
    awaitingSignatureCount: pendingContracts.filter(pc => pc.draftStatus === 'signed_by_person').length,
    contractRejectedCount: pendingContracts.filter(pc => pc.draftStatus === 'draft_rejected').length,
  });

  const todoAction = (id: CompanyTodoId): (() => void) => {
    switch (id) {
      case 'invite': return handleGuideInviteCustomer;
      case 'createProject': return () => navigate('/projects/create');
      case 'kyc':
      case 'bankAccount':
      case 'capabilities': return () => void handleStartKundkannedom();
      case 'vat': return () => navigate('/settings/company?tab=overview');
      case 'practicalInfo': return () => navigate('/settings/company?tab=practical');
      default: return () => navigate('/projects');
    }
  };

  // One "attest" row per stage waiting for the company, naming its project and
  // contract, so each to-do says which contract it belongs to (the contract
  // frames in "Mina Projekt" only point at them).
  const attestRows = (priority: CompanyTodoRow['priority']): CompanyTodoRow[] =>
    pendingAttestationMilestones(allContracts, allMilestones).map((milestone: any) => {
      const contract = allContracts.find((c: any) => c.id === milestone.contractId);
      const project = allProjects.find((p: any) => p.id === contract?.projectId);
      return {
        id: 'attest',
        priority,
        since: milestone.submittedAt ?? milestone.updatedAt,
        key: `attest-${milestone.id}`,
        textKey: 'attestMilestone',
        vars: {
          milestoneNumber: milestone.milestoneNumber,
          milestone: milestone.description ?? '',
          contractNumber: contract?.contractNumber ?? '',
          projectNumber: project?.projectNumber ?? '',
          project: project?.description ?? ''
        },
        onAction: () => navigate(contract?.projectId ? `/projects/${contract.projectId}?tab=timeline` : '/projects?pendingAttestations=true'),
      };
    });

  // One row per contract the customer sent back, opening it straight in the
  // contract editor (ClickUp 869fftzmh).
  const returnedContractRows = (priority: CompanyTodoRow['priority']): CompanyTodoRow[] =>
    pendingContracts.filter(pc => pc.draftStatus === 'draft_rejected').map(pc => {
      const project = allProjects.find((p: any) => p.id === pc.projectId);
      const rejection = (pc.notices ?? []).filter(notice => notice.type === 'rejection')
        .sort((left, right) => right.createdAt.localeCompare(left.createdAt))[0];
      const comment = rejection?.message?.trim();
      return {
        id: 'contractRejected',
        priority,
        since: rejection?.createdAt ?? null,
        key: `contract-returned-${pc.contractId}`,
        textKey: comment ? 'contractReturnedComment' : 'contractReturned',
        vars: {
          contractNumber: pc.contractNumber,
          projectNumber: pc.projectNumber,
          project: project?.description ?? '',
          comment: comment ?? ''
        },
        onAction: () => navigate(`/projects/${pc.projectId}?tab=contract&contractId=${pc.contractId}&edit=1`),
      };
    });

  // One row per stage where the company has to act on an objection, a remedy
  // or a conflict, opening that project's timeline (ClickUp 869fmv7b5).
  const objectionTextKey = { disputed: 'disputedMilestone', underResolution: 'underResolutionMilestone', conflict: 'conflictMilestone' } as const;
  const objectionRows = (priority: CompanyTodoRow['priority']): CompanyTodoRow[] =>
    objectionStages.map(({ milestone, kind }) => {
      const contract = allContracts.find((c: any) => c.id === milestone.contractId);
      const project = allProjects.find((p: any) => p.id === contract?.projectId);
      return {
        id: 'disputed',
        priority,
        since: milestone.disputedAt ?? milestone.updatedAt,
        key: `objection-${milestone.id}`,
        textKey: objectionTextKey[kind],
        vars: {
          milestoneNumber: milestone.milestoneNumber,
          milestone: milestone.description ?? '',
          contractNumber: contract?.contractNumber ?? '',
          projectNumber: project?.projectNumber ?? '',
          project: project?.description ?? ''
        },
        onAction: () => navigate(contract?.projectId ? `/projects/${contract.projectId}?tab=timeline` : '/projects'),
      };
    });

  // One row per contract the customer has signed, opening the company's
  // signing review of it directly (`sign=1`, ClickUp 869fmv7b5).
  const signContractRows = (priority: CompanyTodoRow['priority']): CompanyTodoRow[] =>
    pendingContracts.filter(pc => pc.draftStatus === 'signed_by_person').map(pc => {
      const project = allProjects.find((p: any) => p.id === pc.projectId);
      return {
        id: 'awaitingSignature',
        priority,
        key: `contract-sign-${pc.contractId}`,
        textKey: 'signContract',
        vars: {
          contractNumber: pc.contractNumber,
          projectNumber: pc.projectNumber,
          project: project?.description ?? ''
        },
        onAction: () => navigate(`/projects/${pc.projectId}?tab=contract&contractId=${pc.contractId}&sign=1`),
      };
    });

  const companyTodoRows: CompanyTodoRow[] = [
    ...activeTodos.flatMap((def): CompanyTodoRow[] => {
      switch (def.id) {
        case 'attest': return attestRows(def.priority);
        case 'contractRejected': return returnedContractRows(def.priority);
        case 'disputed': return objectionRows(def.priority);
        case 'awaitingSignature': return signContractRows(def.priority);
        default: return [{ id: def.id, priority: def.priority, onAction: todoAction(def.id) }];
      }
    }),
    ...customerTodoRows
  ];
  useReportTodoCount(companyTodoRows.length);

  // Personal greeting header: the logged-in user's name and how many of their
  // projects are currently ongoing (everything except not-started and completed).
  const greetingName = user?.fullName || companyName || t('home.greeting.fallbackName', 'där');
  const ongoingProjectsCount = allProjects.filter(
    (p: any) => p.status !== 'not_started' && p.status !== 'completed'
  ).length;
  // The tiles in "Dina nyckeltal", in the selected order. Ids without a tile
  // (large blocks, or legacy ids in old admin defaults) are skipped.
  const gridKpis = selectedKpis.filter(id => TILE_KPI_IDS.has(id));

  return (
    <DashboardLayout>
      <MobileCreateProjectButton />
      {/* Personal greeting header on a white card: breadcrumb, "Hej {namn}" and
          the number of ongoing projects. The KPI configuration lives in the
          "Dina nyckeltal" section header below. */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="home-greeting" style={{ minWidth: 0 }}>
            <Breadcrumb items={[{ label: t('nav.home') }]} />
            <h3 className="fw-bold mb-1 mt-2 text-break">
              {t('home.greeting.hello', { name: greetingName, defaultValue: 'Hej {{name}}' })}
            </h3>
            <p className="text-muted mb-0">
              {t('home.greeting.ongoingProjects', { count: ongoingProjectsCount, defaultValue: '{{count}} pågående projekt' })}
            </p>
          </div>
        </div>
      </div>

      {showCompanyGuide && (
        <CompanyOnboardingGuide
          companyName={companyName}
          steps={onboardingSteps}
          showKycStep={showAdyenOnboardingPrompt}
          collapsed={welcomeCollapsed}
          animate={welcomeAnimate}
          onToggleCollapsed={setWelcomeCollapsedByUser}
          onInviteCustomer={handleGuideInviteCustomer}
          onStartKyc={handleStartKundkannedom}
          isStartingKyc={isStartingKundkannedom}
          kycOnboardingUrl={
            adyenAccount?.onboardingUrl && !isMockAdyenOnboardingUrl(adyenAccount.onboardingUrl)
              ? adyenAccount.onboardingUrl
              : null
          }
          kycMessage={kundkannedomMessage}
        />
      )}

      {/* "Att göra nu" (action list) + "Dina nyckeltal" (KPI stat-tiles), 50/50.
          On mobile/tablet-portrait the columns stack in source order, so
          "Att göra nu" sits on top. */}
      <div className="row g-4 mb-4">
        <div className="col-lg-6">
          <SectionPanel
            id={TODO_ANCHOR}
            title={t('home.todoNow.title')}
            icon="clock"
            headerAction={companyTodoRows.length > 0 ? (
              <span className="badge bg-danger flex-shrink-0">
                {t('home.todoNow.count', { count: companyTodoRows.length })}
              </span>
            ) : undefined}
          >
            <CompanyTodoList items={companyTodoRows} />
          </SectionPanel>
        </div>
        <div className="col-lg-6">
          {/* The selected KPIs (admin defaults or "Anpassa") as tiles.
              "Skapa projekt" sits only in the "Kunder utan projekt" tile (the
              menu has its own button). "Anpassa Nyckeltal" is the header row's
              action. */}
          <SectionPanel
            title={t('home.kpis.title')}
            iconClassName="bi bi-graph-up-arrow"
            headerAction={(
              <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 ms-auto mw-100">
                <button
                  type="button"
                  className="btn kpi-configure-btn d-inline-flex align-items-center gap-2 flex-shrink-0"
                  onClick={() => setShowKpiSidebar(true)}
                  aria-haspopup="dialog"
                  aria-expanded={showKpiSidebar}
                  title={t('home.kpis.configureHint')}
                >
                  <i className="feather-settings" aria-hidden="true"></i>
                  {t('home.kpis.configure')}
                </button>
              </div>
            )}
          >
            <CompanyKeyFiguresPanel
              kpiIds={gridKpis}
              projects={allProjects}
              customers={allCustomers}
              contracts={allContracts}
              finance={dashboardFinance}
              activeProjects={ongoingProjectsCount}
              onCreateProject={() => navigate('/projects/create')}
            />
          </SectionPanel>
        </div>
      </div>

      {/* The standalone KYC warning card and the practical-info widget were
          removed: both prompts now live in the "Att göra nu" list above. */}

      <div className="row">
        {/* Left Column (2/3) */}
        <div className="col-lg-8">
          {/* KPI stat-tiles now live in the "Dina nyckeltal" panel directly
              under the welcome widget; the large widgets remain here. */}
          {/* The "Granska Kontrakt" pending-contracts widget was removed: the
              "kontrakt att granska/avvisat" prompts now live in the "Att göra
              nu" list, which deep-links to the filtered project view. */}

          {/* Project Timeline Widget */}
          {/* Mina Projekt is always shown. */}
          {(
            <ProjectTimelineWidget
              onReviseContract={contract => navigate(`/projects/${contract.projectId}?tab=contract&contractId=${contract.id}&edit=1`)}
              className="card"
              projects={allProjects}
              contracts={allContracts as any}
              milestones={allMilestones as any}
              statusSteps={projectStatusMap}
              projectHref={(project) => `/projects/${project.id}`}
              attestationSide="company"
              onMilestonesChanged={(contractId, updated) => setAllMilestones(previous => [
                ...previous.filter(milestone => milestone.contractId !== contractId),
                ...updated
              ])}
              fundingByProject={fundingByProject}
            />
          )}

          {/* Bar Chart - Projects per Status. Top margin only when it follows the
              timeline widget, so it aligns with the right column when it's first. */}
          {selectedKpis.includes('projectStatusChart') && <Card
        title={t('home.chart.title')}
        icon="bar-chart-2"
        className="mt-4"
        headerAction={
          <div className="d-flex gap-3 align-items-center">
            <div className="d-flex align-items-center gap-2">
              <label className="form-label mb-0 text-muted small">{t('home.chart.fromDate')}:</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                style={{ width: '150px' }}
              />
            </div>
            <div className="d-flex align-items-center gap-2">
              <label className="form-label mb-0 text-muted small">{t('home.chart.toDate')}:</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                style={{ width: '150px' }}
              />
            </div>
          </div>
        }
      >
        <div className="py-3">
          {/* Vertical Bar Chart */}
          <div className="d-flex justify-content-around align-items-end" style={{ height: '280px' }}>
            {Object.entries(chartData).map(([status, count]) => {
              const maxCount = getMaxCount();
              const percentage = maxCount > 0 ? (count / maxCount) * 100 : 0;
              const barHeight = Math.max(percentage * 2.2, count > 0 ? 20 : 5);

              return (
                <div
                  key={status}
                  className="d-flex flex-column align-items-center justify-content-end h-100"
                  style={{ flex: 1, maxWidth: '100px', cursor: 'pointer' }}
                  onClick={() => navigate(`/projects?status=${status}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && navigate(`/projects?status=${status}`)}
                >
                  <div className="fw-bold mb-2">{count}</div>
                  <div
                    style={{
                      width: '50px',
                      height: `${barHeight}px`,
                      backgroundColor: statusColors[status],
                      borderRadius: '4px 4px 0 0',
                      transition: 'height 0.5s ease-in-out'
                    }}
                  ></div>
                </div>
              );
            })}
          </div>

          {/* Labels below chart */}
          <div className="d-flex justify-content-around border-top pt-3">
            {Object.entries(statusLabels).map(([status, label]) => (
              <div
                key={status}
                className="text-center small text-muted"
                style={{ flex: 1, maxWidth: '100px', fontSize: '11px' }}
              >
                {label}
              </div>
            ))}
          </div>

          {/* Legend */}
          <div className="d-flex flex-wrap justify-content-center gap-3 mt-4 pt-3 border-top">
            {Object.entries(statusLabels).map(([status, label]) => (
              <div key={status} className="d-flex align-items-center gap-2">
                <div
                  style={{
                    width: '12px',
                    height: '12px',
                    backgroundColor: statusColors[status],
                    borderRadius: '2px'
                  }}
                ></div>
                <span className="small text-muted">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </Card>}
        </div>

        {/* Right Column - Notifications, Welcome and Important Info (1/3) */}
        <div className="col-lg-4">
          {/* Notifications Widget — shared: 3 latest + "Visa fler" (+5) */}
          <NotificationsWidget />

          {/* Invite widget — directly under notifications, in the shared banner. */}
          <div ref={inviteWidgetRef} id="invite-customer-widget">
            <CustomerInviteWidget
              className="mb-4"
              banner
              collapsible
              focusToken={inviteFocusToken}
            />
          </div>

          {(systemInfo as any)?.companyinfo && (
            <Card title={t('home.welcome.title')} icon="home">
              <div
                className="ql-editor p-0"
                dangerouslySetInnerHTML={{ __html: (systemInfo as any).companyinfo }}
              />
            </Card>
          )}
          {systemInfo?.extraimportant && (
            <Card title={t('home.importantInfo.title')} icon="alert-circle">
              <div
                className="ql-editor p-0"
                dangerouslySetInnerHTML={{ __html: systemInfo.extraimportant }}
              />
              {systemInfo.updatedAt && (
                <div className="mt-3 pt-2 border-top">
                  <small className="text-muted">
                    <i className="feather-clock me-1"></i>
                    {t('home.importantInfo.updatedAt')}: {new Date(systemInfo.updatedAt).toLocaleDateString('sv-SE', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </small>
                </div>
              )}
            </Card>
          )}
        </div>
      </div>
      {showKpiSidebar && (
        <KpiSidebar
          selectedKpis={selectedKpis}
          onSave={handleSaveKpis}
          onClose={() => setShowKpiSidebar(false)}
          lang={kpiLang}
        />
      )}
    </DashboardLayout>
  );
}
